/* global Intl */
import { getBackendUrl, isOldBackendSettings } from "../helpFunctions/general";
import supportedLocalesData from "./i18n/supportedLocales.json";

// Locales we can support in scenarios. Canonical source is the schema repo
// (schema/i18n/supportedLocales.json), synced here by `npm run update-schema`.
// The name values are not used, as Intl.DisplayNames will provide the names.
export const supportedLocales = supportedLocalesData;

/**
 * Call with availableLocales to limit, otherwise all supported will be made into options.
 * @param {*} availableLocales
 * @returns
 */
export const formatLocaleOptions = availableLocales => {
  // TODO should superAdmins automatically get all availableLocales ?
  return Object.entries(availableLocales || supportedLocales)
    .filter(([key, val]) => supportedLocales[key])
    .filter(([key, val]) => val !== false && val !== null && val !== undefined)
    .map(([key, val]) => ({
      id: key,
      name: getLocaleDisplayName(key),
      label: getLocaleDisplayName(key),
      value: key,
    }));
};

// Build a mapping from language code (e.g. 'en') to all supported locale codes (e.g. ['en-US','en-GB'])
const buildLanguageMap = () => {
  /** @type {Record<string, string[]>} */
  const map = {};
  Object.keys(supportedLocales || {}).forEach(loc => {
    const [lang] = loc.split("-");
    if (!lang) return;
    if (!map[lang]) map[lang] = [];
    map[lang].push(loc);
  });
  return map;
};

export const languageToLocales = buildLanguageMap();

// Derive a clean base label for a language from the first matching locale name
const baseLabelForLanguage = lang => {
  const firstLocale = (languageToLocales[lang] || [])[0];
  const name = firstLocale ? supportedLocales[firstLocale]?.name || lang : lang;
  // Strip parenthetical qualifiers like "English (British)" -> "English"
  return String(name).split("(")[0].trim() || lang;
};

// Options for selecting by language only (e.g. 'en' -> 'English (any)')
export const languageOptions = Object.keys(languageToLocales)
  .sort()
  .map(lang => {
    const base = baseLabelForLanguage(lang);
    return { id: lang, value: lang, name: `${base} (any)`, label: `${base} (any)` };
  });

// Build a lookup of voice usage counts per voice key across scenarios.
// Accepts either an object map or an array of scenarios.
export const buildVoiceUsageMap = scenarios => {
  /** @type {Record<string, number>} */
  const counts = {};
  if (!scenarios) return counts;
  const list = Array.isArray(scenarios) ? scenarios : Object.values(scenarios);
  list.forEach(scenario => {
    const characters = scenario?.settings?.characters || {};
    const voices = Object.values(characters)
      .map(char => char?.voice || char?.voiceName) // support voice key stored as voice or voiceName
      .filter(Boolean);
    const uniqueVoices = new Set(voices);
    uniqueVoices.forEach(v => {
      counts[v] = (counts[v] || 0) + 1;
    });
  });
  return counts;
};

export const getLocaleDisplayName = localeCode => {
  try {
    const [lang, region] = localeCode.split("-");
    const displayNames = new Intl.DisplayNames("en", { type: "language" });
    const langName = displayNames.of(lang);
    if (region) {
      const regionNames = new Intl.DisplayNames("en", { type: "region" });
      const regionName = regionNames.of(region);
      return `${langName} (${regionName})`;
    }
    return langName || localeCode;
  } catch (e) {
    return localeCode;
  }
};

/**
 * Creates an async function to fetch voice preview audio URL.
 * @param {Object} opts
 * @param {Object} opts.voiceObject - Voice object containing voiceId/id, locales, provider, config
 * @param {string} opts.savedVoiceKey - A saved voice key to reference instead of the voice object (locales/config override if provided).
 * @param {Object} opts.firebase - Firebase instance with authenticatedFetch method
 * @param {Object} opts.settings - Effective backend settings (scenario override merged onto user/org defaults)
 * @param {string} opts.text - Optional text to synthesize (defaults to empty)
 * @returns {Function} Async function that returns the audio URL
 */
export const createVoicePreviewUrlFetcher = ({ voiceObject, savedVoiceKey, firebase, settings, text = "" }) => {
  return async () => {
    const previewText = String(text || "").trim() || "Hello, I'm your AI actor";

    if (isOldBackendSettings(settings)) {
      if (!savedVoiceKey) {
        throw new Error("Legacy backend voice preview requires a saved voice key");
      }
      return getBackendUrl({
        settings,
        path: `voices/${encodeURIComponent(savedVoiceKey)}?text=${encodeURIComponent(previewText)}`,
      });
    }

    // Build request body to match backend expectations
    const body = {};

    if (savedVoiceKey) {
      body.saved_voice = savedVoiceKey;
    }

    if (voiceObject && typeof voiceObject === "object") {
      const voiceId = voiceObject.voiceId || voiceObject.voice_id || voiceObject.id;
      const provider = voiceObject.provider;
      const locales = voiceObject.locales;
      const config = voiceObject.config;

      if (!savedVoiceKey) {
        if (provider) body.provider = provider;
        if (voiceId) body.voice_id = voiceId;
      }

      if (Array.isArray(locales) && locales.length) {
        body.locales = locales;
      }

      if (config && Object.keys(config).length) {
        body.config = config;
      }
    }

    if (text && String(text).trim()) {
      body.text = String(text).trim();
    }

    const url = getBackendUrl({ settings, path: "voices/" });

    const response = await firebase.authenticatedFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate voice preview: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.url) {
      throw new Error("No URL returned from voice preview endpoint");
    }

    return data.url;
  };
};
